// External
import _ from "lodash";
import React from "react";
import ACLStore from "lib/aclStore";
import showdown from "showdown";

// DS
import {
  AddCircleOutlined,
  ArrowLeftPaneOutlined,
  ArrowReversibleOutlined,
  AssessmentAddOutlined,
  ChevronDownCircleOutlined,
  ChevronUpCircleOutlined,
  CopyOutlined,
  GlobeOutlined,
  MagicWandThreeStarsOutlined,
} from "@toddle-edu/ds-icons";
import { colors } from "@toddle-edu/ds-theme";

// Constant
import {
  RUBRIC,
  CHECKLIST,
  SINGLE_POINT_RUBRIC,
  WRITING,
  STREAMING,
  LOADING,
  CURRICULUM_TYPE_DP,
  UNIT_TYPE_DP,
  SUBJECT_TYPE_GENERIC,
  CURRICULUM_TYPE_MYP,
  MYP_INTERDISCIPLINARY,
  TEMPLATE_FIELD_INQUIRY_QUESTIONS,
  TEMPLATE_FIELD_LINES_OF_INQUIRY,
  MYP_OBJECTIVE_RUBRIC,
} from "Constants/stringConstants";
import {
  AI_SUPPORTED_QUESTION_TYPES,
  QUESTION_TYPE_DETAILS,
  LEARNING_GOALS_UID_TAGS_MAPPING,
} from "Worksheet/modules/WorksheetConstants";
import {
  QUESTION_TYPE_UID_VALUE_MAPPING,
  ASSESSMENT_TOOL,
  CONVERT_QUESTION_FORMAT,
  DECREASE_DIFFICULTY,
  ENHANCE_DROPDOWN_LABEL_STYLE,
  GENERATE_SIMILAR_QUESTIONS,
  INCLUDE_REAL_WORLD_EXAMPLES,
  INCREASE_DIFFICULTY,
  CF_QUESTION_TEXT,
  CF_NEW_QUESTION_TYPE,
  WS_AI_ASSESSMENT_TOOLS_MAPPING,
} from "./Constants";
import {
  ASSESSMENT_TOOLS_OBJ,
  getLanguageLocalisedContent,
} from "@toddle-edu/common-utils";

// Utils and services
import {
  htmlToText,
  generateNestedSelectedData,
  getPYPElementSetFromNodes,
  generateRandomId,
} from "Utils";
import { getResolvedOptimisticValue } from "modules/ElementValueUtils";
import {
  getQuestionFromCache,
  getQuestionFromCacheV2,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import { isDateInBetween } from "Utils/DateTimeHelpers";
import {
  getFieldValue,
  getInquiryQuestionsNodes,
} from "Assessments/modules/Utils";
import { getSubFields } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import {
  checkIsMYPRubric,
  getTableDataForMYPRubric,
  preserveAnchorTagFormatter,
} from "../../LessonPlanningAssistant/modules/Utils";
import { isWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";
import { getAssessmentLearningCourseDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";

const _getSelectedFieldHasValue = params => {
  const {
    context,
    fieldList = {},
    assessmentDetails,
    selectedAssessmentField,
  } = params;

  if (!_.isEmpty(context) && !_.isEmpty(context?.[selectedAssessmentField])) {
    switch (selectedAssessmentField) {
      case "textTitles":
        return _.some(
          context?.[selectedAssessmentField],
          val => !_.isEmpty(_.trim(val?.value))
        );
      case "twoTextTitles": {
        const result = _.filter(
          context?.[selectedAssessmentField],
          val => !_.isEmpty(_.trim(val?.value))
        );

        return _.size(result) >= 2;
      }
      default:
        return !_.isEmpty(context?.[selectedAssessmentField]);
    }
  }

  const allFields = _.get(assessmentDetails, "allFields", []);
  const field = _.find(allFields, { uid: selectedAssessmentField });

  const { uid, value } = field || {};
  const { type } = fieldList[uid] || {};

  if (ASSESSMENT_TOOLS_OBJ[selectedAssessmentField]) {
    const assessmentTools = _.get(
      assessmentDetails,
      "assessmentTools.edges",
      []
    );

    const selectedAssessmentTool = _.find(assessmentTools, ({ node }) => {
      return _.get(node, "assessmentToolType") === selectedAssessmentField;
    });

    const assessmentToolItem = _.get(
      selectedAssessmentTool,
      "node.assessmentToolItem",
      {}
    );

    switch (selectedAssessmentField) {
      case RUBRIC: {
        const indicators = _.get(assessmentToolItem, "indicators", []);
        const descriptors = _.get(assessmentToolItem, "descriptors", []);

        const hasIndicators = _.some(
          indicators,
          indicator => !_.isEmpty(indicator?.label)
        );

        const hasDescriptors = _.some(
          descriptors,
          descriptor => !_.isEmpty(descriptor?.label)
        );

        return hasIndicators || hasDescriptors;
      }
      case SINGLE_POINT_RUBRIC: {
        const standards = _.get(assessmentToolItem, "standards", []);

        return _.some(
          standards,
          standard =>
            !_.isEmpty(standard?.title) && !_.isEmpty(standard?.subTitle)
        );
      }
      case CHECKLIST: {
        const checklistItems = _.get(assessmentToolItem, "checklistItems", []);

        return _.some(checklistItems, item => !_.isEmpty(item?.label));
      }
      default:
        return !_.isEmpty(assessmentToolItem);
    }
  }

  switch (type) {
    case "InputNumberList": {
      if (_.every(value, val => _.has(val, "isSelected"))) {
        const selectedValues = _.filter(value, val => val?.isSelected);
        return _.some(selectedValues, val => !_.isEmpty(_.trim(val?.value)));
      } else {
        return _.some(value, val => !_.isEmpty(_.trim(val?.value)));
      }
    }
  }

  switch (uid) {
    case "goals":
      return _.some(value, val => !_.isEmpty(val?.value));
    case "duration":
      return value !== 0;
    case "measureAssessing":
      return false;
    case "inquiryQuestions": {
      return !_.isEmpty(
        _.filter(_.flatten(_.values(value)), item => {
          if (_.has(item, "value")) {
            return !_.isEmpty(item?.value?.trim?.());
          } else {
            return !_.isEmpty(item?.trim?.());
          }
        })
      );
    }
    case "pedagogicalApproachDP": {
      if (_.every(value, key => _.has(key, "value"))) {
        return !_.isEmpty(
          _.filter(value, key => _.get(key, "isSelected", false))
        );
      }

      return !_.isEmpty(value);
    }
    case "content":
    case "disciplinaryContent": {
      if (_.isObject(value)) {
        return _.some(value, val => !_.isEmpty(_.trim(htmlToText(val))));
      }
      return !_.isEmpty(_.trim(htmlToText(value)));
    }
    default: {
      if (_.trim(htmlToText(value)) === "\n") return false;
      return !_.isEmpty(_.trim(htmlToText(value)));
    }
  }
};
export const getSelectedFieldHasValue = params => {
  const { step, stepList } = params;

  if (step) {
    const fields = _.get(stepList, `${step}.fields`, []);

    return _.some(fields, field => {
      return _getSelectedFieldHasValue({
        ...params,
        selectedAssessmentField: field,
      });
    });
  } else {
    return _getSelectedFieldHasValue(params);
  }
};

export const getBlockActions = ({
  isAIAssessmentTools = false,
  isQuestionAssistant = false,
  assessmentToolType,
  t,
}) => {
  let actions = [
    {
      key: "add",
      label: "common:add",
      icon: <AddCircleOutlined />,
      shouldHide: isQuestionAssistant,
    },
    {
      key: "add_or_replace",
      label: "common:add",
      icon: <AddCircleOutlined style={{ color: colors.blue400 }} />,
      shouldHide: !isQuestionAssistant,
    },
    {
      key: "enhance",
      icon: <MagicWandThreeStarsOutlined />,
      shouldHide: !isQuestionAssistant,
    },
  ];

  // * removing all actions and adding assessmentTools actions in case of assessment tool
  if (isQuestionAssistant && !_.isEmpty(assessmentToolType)) {
    actions = [
      {
        key: "add_assessment_tool",
        label: "classRoom:add_to_label",
        fieldType: assessmentToolType,
        localisationParams: {
          label: t("common:question"),
        },
        icon: <ArrowLeftPaneOutlined />,
      },
    ];
  }

  if (isAIAssessmentTools && !_.isEmpty(assessmentToolType)) {
    actions = [
      {
        key: "add_assessment_tool",
        label: "common:add_to_label",
        fieldType: assessmentToolType,
        localisationParams: {
          label: t("common:worksheet_lowercase"),
        },
        icon: <AddCircleOutlined variant="success" />,
      },
    ];
  }

  return _.filter(actions, ({ shouldHide = false }) => !shouldHide);
};

export const getInputForAIResponse = params => {
  const {
    leId,
    content,
    selectedAction,
    gradeId,
    entityType,
    course = {},
    learningCourseId,
    curriculumProgramId,
    curriculumProgramType,
    assessmentDetails,
    unitPlanFields,
    fieldList,
    unitPlanFieldList,
    excludedFields,
    questionId,
    isQuestionAssistant,
    conversationMetadata,
    customInstructions,
    learningGoalsFields,
    conversationId,
    shouldProcessAttachments,
    imageUrls,
  } = params || {};

  let subjects = _.find(assessmentDetails?.allFields, {
    uid: "subjects",
  })?.resolvedMinimalTree?.subjects;

  if (_.isEmpty(subjects) && learningCourseId) {
    const learningCourseDetails = getAssessmentLearningCourseDetailsFromCache({
      id: learningCourseId,
    });

    const learningCourseSubjects = _.get(learningCourseDetails, "subjects", []);
    subjects = learningCourseSubjects;
  }

  const localContext = getLocalStateContext(params);

  const learningGoalsContext =
    getLearningGoalsContext({
      learningGoalsFields,
      unitPlanFields,
      questionText: _.get(localContext, CF_QUESTION_TEXT),
      conversationMetadata,
    }) || {};

  const context = {
    inputText: content,
    grade:
      _.get(course?.primaryGrade, "name") ||
      _.join(_.map(course?.grades, "name"), ", ") ||
      _.join(_.map(assessmentDetails?.grades, "name"), ", "),
    subject: _.join(_.map(subjects, "name"), ", "),
    ...localContext,
    ...learningGoalsContext,
  };

  if (!_.isEmpty(customInstructions)) {
    _.set(context, "customInstructions", customInstructions);
  }

  const questionText = _.get(localContext, CF_QUESTION_TEXT);
  const assessmentId = _.get(assessmentDetails, "id");
  const metadata = {
    gradeId,
    assessmentId,
    conversationId,
    learningCourseId,
    imageUrls,
  };

  if (_.isEmpty(questionText) && !_.isEmpty(questionId)) {
    _.set(metadata, "questionId", questionId);
  }

  let entityId = leId;
  let _entityType = entityType;

  if (isQuestionAssistant) {
    entityId = questionId;
    _entityType = "QUESTION";
  }

  return {
    entityId: entityId,
    entityType: _entityType,
    context,
    metadata,
    action: _.get(selectedAction, "action"),
    fieldType: _.get(selectedAction, "fieldType"),
    curriculumProgramId,
    shouldProcessAttachments,
    excludedFields: getExcludedFields({
      curriculumProgramType,
      unitPlanFields,
      fieldList,
      unitPlanFieldList,
      excludedFields,
    }),
  };
};

export const getRubricDescriptorLabel = params => {
  const { criterias, indicators, descriptors, descriptor } = params;

  return (
    _.find(descriptors, desc => {
      const criteriaLabel = _.find(criterias, {
        id: _.get(desc, "criteria.id"),
      })?.label;
      const indicatorLabel = _.find(indicators, {
        id: _.get(desc, "indicator.id"),
      })?.label;

      return (
        _.get(descriptor, "criteria.label") === criteriaLabel &&
        _.get(descriptor, "indicator.label") === indicatorLabel
      );
    })?.label || ""
  );
};

export const getFormattedResponse = ({
  content,
  fieldType,
  assessmentDetails,
}) => {
  try {
    const convertedHtml = convertMarkdownToHTML({ content });

    const tableRows = convertedHtml.match(/<tr>(.*?)<\/tr>/gs);

    switch (fieldType) {
      case CHECKLIST: {
        const checklistOptions = tableRows[0]
          .match(/<th.*?<\/th>/g)
          .slice(1)
          .map(header => {
            return {
              label: htmlToText(header.replace(/<\/?[^>]+(>|$)/g, "").trim()),
            };
          });

        const criterias = _.reduce(
          tableRows,
          (criterias, row) => {
            const cells = row.match(/<td.*?<\/td>/g);
            if (!cells) return criterias;

            const firstCell = cells[0];
            const firstCellData = firstCell
              .replace(/<\/?[^>]+(>|$)/g, "")
              .trim();

            if (
              _.isEmpty(checklistOptions) ||
              _.first(checklistOptions).label === "Options"
            ) {
              const secondCell = cells[1];
              const secondCellData = secondCell.replace(/<\/?td>/g, "");

              const splitedByCharacter = _.includes(secondCellData, "&#124;")
                ? "&#124;"
                : "|";

              const options = secondCellData
                .split(splitedByCharacter)
                .map(item => ({ label: item.trim() }));

              checklistOptions.push(...options);
            }

            criterias.push({ label: htmlToText(firstCellData) });
            return criterias;
          },
          []
        );

        return {
          criterias,
          checklistOptions,
        };
      }
      case RUBRIC: {
        const descriptorLevels = tableRows[0]
          .match(/<th.*?<\/th>/g)
          .slice(1)
          .map((header, index) => {
            return {
              label: htmlToText(header.replace(/<\/?[^>]+(>|$)/g, "").trim()),
              id: index + 1,
            };
          });

        const { criterias, descriptors } = _.reduce(
          tableRows,
          (criteriaDescriptorsObj, row, rowIdx) => {
            const cells = row.match(/<td.*?<\/td>/g);
            if (!cells) return criteriaDescriptorsObj;

            const firstCell = cells[0];
            const firstCellData = firstCell
              .replace(/<\/?[^>]+(>|$)/g, "")
              .trim();

            criteriaDescriptorsObj.criterias.push({
              label: htmlToText(firstCellData),
              id: rowIdx,
            });

            _.forEach(cells.slice(1), (cell, index) => {
              const cellData = cell.replace(/<\/?[^>]+(>|$)/g, "").trim();

              criteriaDescriptorsObj.descriptors.push({
                label: htmlToText(cellData),
                descriptorLevelId: index + 1,
                criteriaId: rowIdx,
              });
            });

            return criteriaDescriptorsObj;
          },
          {
            criterias: [],
            descriptors: [],
          }
        );

        return {
          criterias,
          descriptorLevels,
          descriptors,
        };
      }
      case SINGLE_POINT_RUBRIC: {
        const criterias = tableRows[0]
          .match(/<th.*?<\/th>/g)
          .map((header, index) => {
            const title = htmlToText(
              header.match(/<strong>(.*?)<\/strong>/)?.[1]
            );
            const subTitle = htmlToText(
              header
                .replace(/<\/?[^>]+(>|$)/g, "")
                .trim()
                .replace(title, "")
                .trim()
                .replace(/^:\s*/, "")
            );
            return {
              title,
              subTitle,
              isCriteriaStandardColumn: index === 1,
              __typename: "SinglePointRubricCriteria",
            };
          });

        const standards = tableRows.slice(1).map(row => {
          const cells = row.match(/<td.*?<\/td>/g);
          const cell = cells[1];
          const title = htmlToText(cell.match(/<strong>(.*?)<\/strong>/)?.[1]);
          const subTitle = htmlToText(
            cell
              .replace(/<\/?[^>]+(>|$)/g, "")
              .trim()
              .replace(title, "")
              .trim()
              .replace(/^:\s*/, "")
          );
          return { title, subTitle, __typename: "SinglePointRubricStandard" };
        });

        return {
          criterias,
          standards,
        };
      }
      case MYP_OBJECTIVE_RUBRIC: {
        const parser = new DOMParser();
        const doc = parser.parseFromString(convertedHtml, "text/html");

        const tableData = getTableDataForMYPRubric({
          doc,
          assessmentDetails,
          type: fieldType,
        });

        return _.map(tableData, ({ headers, rows, subject }) => {
          return _.map(rows, row => {
            return _.map(row, (cell, index) => {
              return {
                achievementLevel: headers[index],
                label: cell,
                subject,
              };
            });
          });
        });
      }
      default:
        return {};
    }
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getFormattedResponseMemoized = _.memoize(
  params => getFormattedResponse(params),
  ({ content, fieldType, assessmentDetails }) =>
    JSON.stringify({
      content,
      fieldType,
      assessmentDetails: _.map(assessmentDetails, "allFields", []),
    })
);

export const getCombinedPrompts = params => {
  const {
    t,
    constantPrompts,
    language,
    subjects,
    subjectGroups,
    curriculumProgramType,
    showAllOptions = false,
  } = params;

  const constantPromptsGroup = _.groupBy(constantPrompts, "groupType");

  const isEnglishLanguageSelected = language === "en";

  const result = [];
  _.forEach(constantPromptsGroup, (value, key) => {
    const innerOptions = getUpdatedPrompts({
      t,
      value,
      subjects,
      subjectGroups,
      isEnglishLanguageSelected,
      curriculumProgramType,
    });

    const displayPrompt = _.find(value, { promptType: "display" });
    const label =
      isEnglishLanguageSelected || _.isNil(displayPrompt?.displayTextLocale)
        ? displayPrompt?.displayText
        : t(displayPrompt?.displayTextLocale);

    const options = showAllOptions
      ? innerOptions
      : [
          ..._.slice(innerOptions, 0, 2),
          {
            id: "MORE",
            label: t("common:lowercase", {
              text: `${t("common:more")} +`,
            }),
          },
        ];

    if (label && _.size(innerOptions) > 0) {
      const displayPrompt = _.find(constantPrompts, {
        groupType: key,
        promptType: "display",
      });

      const { id, displaySequence = 0 } = displayPrompt || {};

      result.push({
        id: key,
        displayPromptId: id,
        options,
        label,
        displaySequence,
      });
    }
  });

  return _.sortBy(result, "displaySequence");
};

export const getIsAIResponseLoading = state => {
  return [WRITING, STREAMING, LOADING].includes(state);
};

export const convertMarkdownToHTML = ({
  content,
  shouldParseContent = false,
}) => {
  const converter = new showdown.Converter({
    tables: true,
  });

  if (shouldParseContent) {
    const parsedContent = _.replace(content, /###/g, "@@@")?.trim();
    const covertedText = converter.makeHtml(parsedContent);

    return _.replace(covertedText, /@@@/g, "###");
  }

  return converter.makeHtml(content);
};

export const getIsListTitleBold = string => {
  const htmlString = convertMarkdownToHTML({ content: string });
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  const paragraphs = doc.getElementsByTagName("p");

  for (let i = 0; i < paragraphs?.length; i++) {
    const paragraph = paragraphs?.[i];
    const directChildren = paragraph?.children;

    for (let j = 0; j < directChildren?.length; j++) {
      const child = directChildren?.[j];
      if (child?.tagName?.toLowerCase() === "strong") {
        return true;
      }
    }
  }

  return false;
};

export const getTemplateArray = params => {
  return _.filter(params, param => !_.isEmpty(param));
};

const checkIsSinglePointRubric = table => {
  const rows = table.querySelectorAll("tr");

  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i].querySelectorAll("td");
    if (_.size(cells) !== 3) {
      return false;
    }

    // Check if first and last cells are empty
    const firstCellIsEmpty = cells[0].innerText.trim() === "";
    const lastCellIsEmpty = cells[cells.length - 1].innerText.trim() === "";

    if (!firstCellIsEmpty || !lastCellIsEmpty) {
      return false;
    }
  }

  return true;
};

const checkIsChecklist = table => {
  // Get the column headers
  const thElements = table.querySelectorAll("th");
  const columnHeaders = [...thElements].map(th => {
    if (th.innerText.trim()) {
      return th.innerText.trim();
    }
  });
  const numColumns = columnHeaders.length;

  // Get the data in each column
  const columns = [];
  for (let i = 0; i < numColumns; i++) {
    columns.push([]);
  }

  const tdElements = table.querySelectorAll("td");
  tdElements.forEach((td, index) => {
    const columnIdx = index % numColumns;
    if (td.innerText.trim()) {
      columns[columnIdx].push(td.innerText.trim());
    }
  });

  if (_.every(_.tail(columns), column => _.isEmpty(column))) return true;

  if (_.some(columns, column => _.isEmpty(column))) return false;

  // Check if all columns (except the first column) have the same data
  const areColumnsSame = columns.slice(1).every((column, index) => {
    const columnHeader = columnHeaders[index + 1]; // +1 to skip the first column header
    return column.every(value => value === columnHeader);
  });

  return areColumnsSame;
};

const checkIsRubric = ({ language, table }) => {
  // Check if the first cell contains "Criteria"

  const headers = table.querySelectorAll("th");

  if (_.some(headers, header => _.isEmpty(header?.innerText?.trim())))
    return false;

  const criteriaLabel = _.get(
    getLanguageLocalisedContent(language),
    "criteria"
  );

  // Check if the first row's first cell contains the text "Criteria"
  const firstCellFirstRow = headers[0];
  if (
    !firstCellFirstRow ||
    firstCellFirstRow.innerText.trim() !== criteriaLabel
  ) {
    return false;
  }

  return true;
};

export const getToolTypeFromContent = params => {
  const { language, content, assessmentDetails = {}, metadata = {} } = params;

  const contentHTML = convertMarkdownToHTML({ content });

  const parser = new DOMParser();
  const doc = parser.parseFromString(contentHTML, "text/html");

  const table = doc.querySelector("table");

  if (table) {
    if (checkIsChecklist(table)) return CHECKLIST;
    else if (checkIsSinglePointRubric(table)) return SINGLE_POINT_RUBRIC;
    else if (checkIsRubric({ language, table })) return RUBRIC;
    else if (
      checkIsMYPRubric({
        doc,
        assessmentDetails,
        metadata,
        type: MYP_OBJECTIVE_RUBRIC,
      })
    ) {
      return MYP_OBJECTIVE_RUBRIC;
    }
  }

  return null;
};

export const getEmojisAndTextFromString = string => {
  // Regular expression to match emojis
  const emojiRegex = /[\p{Emoji}\u200d]+/gu;

  const emojis = string.match(emojiRegex) || [];

  const textWithoutEmojis = string.split(emojiRegex);

  return {
    emoji: _.find(emojis, emoji => !_.isEmpty(emoji)),
    text: _.compact(textWithoutEmojis)
      .find(text => text?.trim() !== "" && text !== "‍")
      ?.trim(),
  };
};

export const checkIsLEAIUserMessageEditEnabled = () => {
  return ACLStore.can("FeatureFlag:LEAIUserMessageEdit");
};

const buildPlannerNodesString = (nodes, currentContext = "") => {
  _.forEach(nodes, node => {
    const { label, children: childrenNodes, id } = node || {};

    if (label && _.isEmpty(childrenNodes)) {
      currentContext += `--- (${id}) ${label}\n`;
    } else {
      if (!_.isEmpty(childrenNodes)) {
        currentContext += `${label}\n`;
        currentContext = buildPlannerNodesString(childrenNodes, currentContext);
      }
    }
  });

  return currentContext;
};

export const getLearningGoalsContext = params => {
  const {
    learningGoalsFields,
    unitPlanFields,
    questionText,
    conversationMetadata,
  } = params;

  const context = {};
  const learningGoalsFieldsUIDs = _.keys(learningGoalsFields);

  // if questionText empty then send learning goals from conversations' metadata
  if (_.isEmpty(questionText)) {
    const conversationAllFields = _.get(conversationMetadata, "allFields", []);

    // add learning goals one by one in context
    _.forEach(learningGoalsFieldsUIDs, fieldUID => {
      const fieldData = _.find(conversationAllFields, {
        uid: fieldUID,
      });

      if (!_.isEmpty(fieldData)) {
        let combinedString = "";

        switch (fieldUID) {
          case TEMPLATE_FIELD_INQUIRY_QUESTIONS:
            {
              // getting nodes for inquiry questions, as structure of it is different
              const { nodes } = getInquiryQuestionsNodes({
                unitPlanFields: unitPlanFields,
                assessmentFields: conversationAllFields,
                fieldKey: fieldUID,
                subFields: getSubFields({
                  fieldKey: fieldUID,
                  fieldObj: _.get(learningGoalsFields, fieldUID, {}),
                  unitPlanFields: unitPlanFields,
                }),
                mode: "view",
              });

              combinedString = buildPlannerNodesString(nodes);
            }
            break;
          case TEMPLATE_FIELD_LINES_OF_INQUIRY:
            {
              const liosData = _.get(
                _.filter(unitPlanFields, data =>
                  _.isEqual(TEMPLATE_FIELD_LINES_OF_INQUIRY, data.uid)
                ),
                "[0].value",
                []
              );
              const conversationAllFields = _.get(
                conversationMetadata,
                "allFields",
                []
              );
              const selectedFields = _.get(
                _.filter(conversationAllFields, obj =>
                  _.isEqual(fieldUID, obj.uid)
                ),
                "[0].value",
                []
              );
              const filteredObjects = _.filter(liosData, obj =>
                _.includes(selectedFields, obj.id)
              );

              combinedString = _.join(_.map(filteredObjects, "value"), "\n");
            }
            break;

          default:
            {
              const { value, resolvedMinimalTree } = fieldData || {};

              const uid = fieldUID === "lp" ? "learnerProfiles" : fieldUID;
              const nodeSet = getPYPElementSetFromNodes({
                nodes: _.get(
                  resolvedMinimalTree,
                  "nodes",
                  _.get(resolvedMinimalTree, uid, [])
                ),
                ...(uid === "benchmarks" && { startDepth: 1 }),
              });

              const nestedNodes = generateNestedSelectedData({
                ...(nodeSet || {}),
                value,
              });

              combinedString = buildPlannerNodesString(nestedNodes);
            }
            break;
        }

        if (!_.isEmpty(_.trim(combinedString))) {
          _.set(context, fieldUID, combinedString);
        }
      }
    });
  }
  // If questionText present then read learning goals content from it and add
  else {
    _.forEach(learningGoalsFieldsUIDs, fieldUID => {
      const content = getLearningGoalsContentFromQuestionText(
        questionText,
        LEARNING_GOALS_UID_TAGS_MAPPING[fieldUID]
      );

      if (!_.isEmpty(content)) {
        _.set(context, fieldUID, content);
      }
    });
  }

  return context;
};

const getLocalStateContext = params => {
  const { context } = params;

  const result = {};

  _.forEach(context, (value, key) => {
    if (_.isEmpty(value)) return;

    switch (key) {
      case "textTitles":
      case "twoTextTitles": {
        result[key] = _.join(
          _.map(value, (item, index) => {
            return `${index + 1}. ${item?.value}.`;
          }),
          "\n"
        );
        break;
      }
      default: {
        if (_.isArray(value)) {
          result[key] = _.join(value, ", ");
        } else {
          result[key] = value;
        }
      }
    }
  });

  return result;
};

export const getExcludedFields = params => {
  const {
    curriculumProgramType,
    unitPlanFields,
    fieldList = [],
    unitPlanFieldList,
    excludedFields,
  } = params;

  const localExcludedFields = [
    ...(_.map(excludedFields, field => _.last(_.split(field, "_"))) || {}),
  ];

  const unitType = getFieldValue({
    fields: unitPlanFields,
    uid: "unitType",
  });

  const learningGoalsFields = _.map(
    _.get(fieldList, "addLearningGoals.config.options"),
    "key"
  );

  const commonExcludedFields = _.reduce(
    learningGoalsFields,
    (acc, field) => {
      if (!_.isEmpty(unitPlanFieldList)) {
        const currentField = _.find(unitPlanFieldList, (val, key) => {
          return _.isEqual(key, field) || _.startsWith(key, field);
        });

        if (_.isEmpty(currentField)) {
          acc.push(field);
        }
      }
      return acc;
    },
    []
  );

  if (!_.isEmpty(commonExcludedFields)) {
    localExcludedFields.push(...commonExcludedFields);
  }

  // if unit type is not interdisciplinary then exclude interdisciplinary criteria
  if (
    curriculumProgramType === CURRICULUM_TYPE_MYP &&
    !_.isEmpty(unitType) &&
    unitType !== MYP_INTERDISCIPLINARY
  ) {
    localExcludedFields.push("interdisciplinaryCriteriaMYP");
  }

  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_DP: {
      const unitType = _.get(
        _.find(unitPlanFields, { uid: "unitType" }),
        "value"
      );

      const subjects = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "resolvedMinimalTree.subjects",
        []
      );

      const isSubjectTypeGeneric =
        _.get(_.first(subjects), "type", SUBJECT_TYPE_GENERIC) ===
        SUBJECT_TYPE_GENERIC;

      if (unitType == UNIT_TYPE_DP && !isSubjectTypeGeneric) {
        return [...localExcludedFields, "subjectLevel", "unitSubjectLevel"];
      }
      return localExcludedFields;
    }
    default:
      return localExcludedFields;
  }
};

const getSubjectItem = params => {
  const { curriculumProgramType, subjects, subjectGroups } = params;

  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_MYP:
      return _.map(subjectGroups, ({ globalSubjectGroup }) =>
        _.get(globalSubjectGroup, "constants.iconKey")
      );
    default:
      return _.map(subjects, "name");
  }
};

export const getUpdatedPrompts = params => {
  const {
    t,
    value,
    isEnglishLanguageSelected,
    isAIAssessmentTools = false,
  } = params;

  return _.chain(_.orderBy(value, "fieldType"))
    .filter(item => {
      if (!isAIAssessmentTools) return true;
      return _.has(WS_AI_ASSESSMENT_TOOLS_MAPPING, item?.fieldType);
    })
    .map(item => {
      const {
        isTimeDependent = false,
        startDate,
        endDate,
        displayText,
        displayTextLocale,
      } = item || {};

      const label = isAIAssessmentTools
        ? t(_.get(WS_AI_ASSESSMENT_TOOLS_MAPPING, item?.fieldType))
        : isEnglishLanguageSelected || _.isNil(displayTextLocale)
        ? displayText
        : t(displayTextLocale);

      let isNewTagVisible = false;

      if (isTimeDependent && !_.isNil(startDate) && !_.isNil(endDate)) {
        isNewTagVisible = isDateInBetween({
          date1: startDate,
          date2: endDate,
        });
      }

      return {
        ...item,
        key: item?.id,
        label,
        isNewTagVisible,
      };
    })
    .filter(item => {
      const { fieldType } = item || {};
      const [group, subGroup] = _.split(fieldType, "_");

      return (
        item?.promptType === "user" &&
        (group !== "SUBJECT" || _.includes(getSubjectItem(params), subGroup))
      );
    })
    .sortBy(item => {
      const { fieldType, displaySequence, isNewTagVisible } = item || {};

      const [group] = _.split(fieldType, "_");
      const isSubject = group === "SUBJECT";

      return [isNewTagVisible ? 0 : isSubject ? 1 : 2, displaySequence];
    })
    .value();
};

export const getEnhancePromptsOptions = params => {
  const {
    t,
    constantPrompts,
    language,
    subjects,
    subjectGroups,
    curriculumProgramType,
    questionTypeUID,
  } = params;

  const enhanceOptionPrompts = _.filter(constantPrompts, {
    groupType: "ENHANCE_OPTION",
  });

  const isEnglishLanguageSelected = language === "en";

  const removePromptsByActions = [];

  let promptOptions = getUpdatedPrompts({
    t,
    value: enhanceOptionPrompts,
    subjects,
    subjectGroups,
    isEnglishLanguageSelected,
    curriculumProgramType,
  });

  // * combine increase and descrease difficulty prompt
  const increaseDifficulty = _.find(promptOptions, {
    action: INCREASE_DIFFICULTY,
  });
  const decreaseDifficulty = _.find(promptOptions, {
    action: DECREASE_DIFFICULTY,
  });

  if (!_.isEmpty(increaseDifficulty) && !_.isEmpty(decreaseDifficulty)) {
    const changeDifficultyPrompt = {
      label: "Change difficulty",
      componentType: "increase_decrease_difficulty",
      prompts: { increaseDifficulty, decreaseDifficulty },
    };

    promptOptions.splice(1, 0, changeDifficultyPrompt);

    removePromptsByActions.push(INCREASE_DIFFICULTY, DECREASE_DIFFICULTY);
  }

  // * group assessment tool propts
  const combinedPrompts = getCombinedPrompts({
    t,
    constantPrompts,
    language,
    subjectGroups,
    subjects,
    curriculumProgramType,
    showAllOptions: true,
  });

  const assessmentToolsPrompt = _.find(combinedPrompts, {
    id: "ASSESSMENT_TOOL",
  });

  if (!_.isEmpty(assessmentToolsPrompt)) {
    assessmentToolsPrompt["componentType"] = "dropdown";
    promptOptions.push(assessmentToolsPrompt);
  }

  // * convert to alternate question types
  const convertToAlternatePrompt = _.find(promptOptions, {
    action: CONVERT_QUESTION_FORMAT,
  });

  if (!_.isEmpty(convertToAlternatePrompt)) {
    convertToAlternatePrompt["componentType"] = "dropdown";
    // * adding all alternate question type option, except active question type
    convertToAlternatePrompt["options"] = _.filter(
      getAlternateQuestionTypes(t),
      questionType => !_.isEqual(questionTypeUID, _.get(questionType, "key"))
    );
  }

  promptOptions = _.filter(
    promptOptions,
    promptOption =>
      !_.includes(removePromptsByActions, _.get(promptOption, "action"))
  );

  return promptOptions;
};

export const getQuestionActionOptions = t => [
  {
    key: "replace",
    label: t("common:replace_with_label", { label: t("common:question") }),
  },
  {
    key: "add",
    label: t("common:add_as_a_new_label", { label: t("common:question") }),
  },
];

export const getWorksheetAssistantContainerStyle = (
  isQuestionAssistant = false
) => ({
  backgroundColor: colors.surfaceWhite,
  minWidth: isQuestionAssistant ? "520px" : "610px",
  maxWidth: isQuestionAssistant ? "520px" : "720px",
  borderLeft: `1px solid ${colors.borderSubtle}`,
});

export const getWorksheetAIAssessmentToolsStyle = () => ({
  backgroundColor: colors.surfaceWhite,
  minWidth: "456px",
  maxWidth: "456px",
  borderLeft: `1px solid ${colors.borderSubtle}`,
});

export const getLearningGoalsContentFromQuestionText = (
  questionText = "",
  learningGoalType
) => {
  try {
    const regex = new RegExp(
      `<${learningGoalType}>([\\s\\S]*?)<\\/${learningGoalType}>`
    );
    const match = questionText.match(regex);
    const content = match ? match[1].trim() : null;

    return content;
  } catch (e) {
    return "";
  }
};

/**
 * @description generating icon with label
 * @param {*} label
 * @param {*} icon
 * @returns
 */
const getLabelWithIcon = ({ label, icon }) => (
  <div style={ENHANCE_DROPDOWN_LABEL_STYLE}>
    {React.isValidElement(icon) ? icon : null} {label}
  </div>
);

/**
 * @description generating option for dropdown from enhance prompts
 * @param {Array} enhancePrompts - constant AI enhance prompts
 * @param {String} questionTypeUID - active question type UID
 * @returns array of enhance prompts
 */
export const getEnhanceOptions = _.memoize(
  ({ enhancePrompts, questionTypeValue, t }) => {
    return _.map(enhancePrompts, prompt => {
      const action = _.get(prompt, "action");

      const icon = getPromptIcon({
        key: action,
        size: "xx-small",
        iconStyle: { color: "unset" },
      });

      let promptOption = {
        key: action,
        label: getLabelWithIcon({ label: _.get(prompt, "displayText"), icon }),
        action,
      };

      // * adding alternate question types options as sub-menu
      if (_.isEqual(CONVERT_QUESTION_FORMAT, action)) {
        promptOption = {
          ...promptOption,
          label: getLabelWithIcon({ label: "Convert to", icon }),
          // * adding all alternate question type option, except generated question type
          options: _.filter(
            getAlternateQuestionTypes(t),
            questionType =>
              !_.isEqual(
                _.lowerCase(questionTypeValue),
                _.lowerCase(_.get(questionType, "value"))
              )
          ),
          isSubMenu: true,
        };
      }

      return promptOption;
    });
  },
  ({ enhancePrompts, questionTypeValue }) =>
    JSON.stringify({
      enhancePrompts: _.map(enhancePrompts, prompt => ({
        action: _.get(prompt, "action"),
        displayText: _.get(prompt, "displayText"),
      })),
      questionTypeValue,
    })
);

/**
 * @description extracting question type from AI generated question
 * @param {*} content - AI generated question content
 * @returns {string} question type
 */
export const getTypeOfQuestionFromContent = content => {
  const regex = /<typeOfQuestion>\s*([\s\S]*?)\s*<\/typeOfQuestion>/;

  const match = content.match(regex);

  if (!_.isEmpty(match)) {
    return _.trim(_.get(match, [1], ""));
  } else {
    const regex2 = /<toq>\s*([\s\S]*?)\s*<\/toq>/;

    const match2 = content.match(regex2);
    if (!_.isEmpty(match2)) {
      return _.trim(_.get(match2, [1], ""));
    }
  }

  return "";
};

/**
 * @description providing icon based on key
 * @param {string} key - it can be action, id.
 * @param {string} size - icon size
 * @param {object} iconStyle - style properties for icon
 * @returns {JSX.Element} - icon element
 */
export const getPromptIcon = ({
  key,
  size,
  iconStyle = { color: colors.neutral400 },
}) => {
  const iconSize = _.isEmpty(size) ? "xxx-small" : size;

  switch (key) {
    case GENERATE_SIMILAR_QUESTIONS:
      return <CopyOutlined size={iconSize} style={iconStyle} />;

    case INCLUDE_REAL_WORLD_EXAMPLES:
      return <GlobeOutlined size={iconSize} style={iconStyle} />;

    case INCREASE_DIFFICULTY:
      return <ChevronUpCircleOutlined size={iconSize} style={iconStyle} />;

    case DECREASE_DIFFICULTY:
      return <ChevronDownCircleOutlined size={iconSize} style={iconStyle} />;

    case CONVERT_QUESTION_FORMAT:
      return <ArrowReversibleOutlined size={iconSize} style={iconStyle} />;

    case ASSESSMENT_TOOL:
      return <AssessmentAddOutlined size={iconSize} style={iconStyle} />;

    default:
      return null;
  }
};

/**
 * @description based on key, checking whether it is `CONVERT_QUESTION_FORMAT` action or not
 * @param {string} key - key of selected question type
 * @param {Function} t - translation util
 * @returns {boolean} return true if action is for convertToAlternate question
 */
export const isConvertToAlternateAction = (key, t) => {
  return _.some(getAlternateQuestionTypes(t), { key });
};

/**
 * @description creating array of AI supported question types
 * @param {Function} t - translation util
 * @returns {Object[]} array of AI supported question types
 */
export const getAlternateQuestionTypes = t => {
  const ALTERNATE_QUESTION_TYPES_DETAILS = [];

  _.forEach(AI_SUPPORTED_QUESTION_TYPES, questionTypeUID => {
    const questionTypeDetails = QUESTION_TYPE_DETAILS[questionTypeUID];

    if (!_.isEmpty(questionTypeDetails)) {
      const perm = _.get(questionTypeDetails, "perm");
      const canAdd = _.isEmpty(perm) ? true : ACLStore.can(perm);
      let icon =
        _.get(questionTypeDetails, "icon2") ||
        _.get(questionTypeDetails, "icon");

      if (React.isValidElement(icon)) {
        icon = React.cloneElement(icon, { style: { color: "unset" } });
      }

      const label =
        t(_.get(questionTypeDetails, "localeLabel2")) ||
        t(_.get(questionTypeDetails, "localeLabel"));

      if (canAdd) {
        ALTERNATE_QUESTION_TYPES_DETAILS.push({
          key: questionTypeUID,
          value: QUESTION_TYPE_UID_VALUE_MAPPING[questionTypeUID],
          label: getLabelWithIcon({ label, icon }),
        });
      }
    }
  });

  return ALTERNATE_QUESTION_TYPES_DETAILS;
};

/**
 * @description fetching assessment tools from question cache data by questionId
 * @param {string} questionId - active question id
 * @returns action question's assessment tools
 */
export const getAssessmentToolsFromQuestion = questionId => {
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

  const getQuestion = isQueriesV2Enabled
    ? getQuestionFromCacheV2
    : getQuestionFromCache;

  const questionFromCachedData = getQuestion({ id: questionId });

  const assessmentTools = _.get(
    questionFromCachedData,
    "assessmentTools.edges",
    []
  );

  return assessmentTools;
};

/**
 * @description checking tool exist on question or not
 * @param {string} questionId - active question id
 * @param {string} assessmentToolType - assessment tool type, i.e. CHECKLIST, RUBRIK...
 * @returns true if tool exist on question else false
 */
export const isAssessmentToolsAlreadyExist = ({
  questionId,
  assessmentToolType,
}) => {
  const assessmentTools = getAssessmentToolsFromQuestion(questionId);

  const selectedAssessmentTool = _.find(assessmentTools, ({ node }) => {
    return _.get(node, "assessmentToolType") === assessmentToolType;
  });

  return !_.isEmpty(selectedAssessmentTool);
};

export const getImprovePrompts = ({
  t,
  constantPrompts,
  subjects,
  subjectGroups,
  language,
  curriculumProgramType,
}) => {
  const improvePrompts = _.filter(constantPrompts, { fieldType: "IMPROVE" });

  const isEnglishLanguageSelected = language === "en";

  const updatedPrompts = getUpdatedPrompts({
    t,
    value: improvePrompts,
    subjects,
    subjectGroups,
    isEnglishLanguageSelected,
    curriculumProgramType,
  });

  return updatedPrompts;
};

// getting text from selectedAction or inputbox for AI input

/**
 * @description parsing content, customInstructions and inputText from query and selectedAction for AI input
 * @param {Function} t - utility function for localisation
 * @param {Object} selectedAction - selected prompt action
 * @param {boolean} isEnglishLanguageSelected - flag for english language
 * @param {Object} editUserPromptObj - user prompt object
 * @param {string} query - input box text
 * @returns content, customInstructions and inputTex
 */

export const getContentToSend = params => {
  const {
    t,
    selectedAction,
    isEnglishLanguageSelected,
    editUserPromptObj,
    query,
    context,
    conversationAttachments,
  } = params;

  // @reference from src/routes/routes/Teacher/routes/Courses/routes/Course/routes/ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/Utils.js
  let parsedQuery = query;
  // extra </span>. is added because the regex in 'replace func' below replaces string till the last closing span tag
  const replaceWith = !_.isEmpty(conversationAttachments)
    ? _.map(
        conversationAttachments,
        attachment =>
          `<span><a href="${attachment.url}" target="_blank" rel="noopener noreferrer">${attachment.name}</a></span>`
      ).join(", ") + "</span>."
    : "</span>.";

  // replaces <span> tag which has id="attachment-text" with anchor tag
  parsedQuery = _.replace(
    query,
    /<span\s+[^>]*id=["']attachment-text["'][^>]*>[\s\S]*<\/span>/gi,
    replaceWith
  );

  const htmlToTextConfig = {
    wordwrap: false,
    formatters: {
      anchorTagFormatter: preserveAnchorTagFormatter,
    },
    tags: {
      a: {
        format: "anchorTagFormatter",
      },
    },
  };
  parsedQuery = _.trim(htmlToText(parsedQuery, htmlToTextConfig));

  if (!_.isEmpty(selectedAction)) {
    let content;

    const actionKey = _.get(selectedAction, "action");
    const chatText = getPromptChatText({
      key: actionKey,
      t,
      type: _.get(context, CF_NEW_QUESTION_TYPE, ""),
    });

    if (!_.isEmpty(chatText)) {
      content = chatText;
    } else {
      content = t(selectedAction?.displayTextLocale);

      if (
        isEnglishLanguageSelected ||
        _.isNil(selectedAction?.displayTextLocale)
      ) {
        content = selectedAction?.displayText;
      }
    }

    const tempElement = document.createElement("div");
    tempElement.innerHTML = query;
    const userInstructions = _.trim(
      _.get(tempElement, "lastChild.textContent")
    );

    const shouldAddSemiColon = !_.isEmpty(userInstructions);

    return {
      content: shouldAddSemiColon ? `${_.trim(content)};` : _.trim(content),
      inputText: _.trim(`${_.trim(content)} ${userInstructions}`),
      customInstructions: userInstructions,
    };
  } else {
    return {
      content: editUserPromptObj.userPrompt || _.cloneDeep(parsedQuery),
      inputText: _.trim(
        `${editUserPromptObj.userPrompt} ${_.cloneDeep(parsedQuery)}`
      ),
      customInstructions:
        editUserPromptObj.userPrompt || _.cloneDeep(parsedQuery),
    };
  }
};

/**
 * creating filter object for AIPrompts query
 * @param {object} assessmentDetails
 * @param {string} curriculumType
 * @returns filter object for AIPrompts
 */
export const getAIPromptsFilter = (assessmentDetails, curriculumType) => {
  const aiPromptsFilter = {};

  const subjectsField = _.find(_.get(assessmentDetails, "allFields", []), {
    uid: "subjects",
  });

  switch (curriculumType) {
    case CURRICULUM_TYPE_MYP:
      {
        const subjectGroups = _.map(
          _.get(subjectsField, "resolvedMinimalTree.subjects", []),
          "subjectGroup"
        );

        const subjectGroupsIds = _.map(subjectGroups, "id");
        _.set(aiPromptsFilter, "subjectGroupIds", subjectGroupsIds);
      }
      break;

    case CURRICULUM_TYPE_DP:
      {
        const subjectsValue = _.get(subjectsField, "value", []);
        _.set(aiPromptsFilter, "subjectIds", subjectsValue);
      }
      break;

    default:
      break;
  }

  return aiPromptsFilter;
};

export const getPromptChatText = ({ key, t, type = "" }) => {
  switch (key) {
    case GENERATE_SIMILAR_QUESTIONS:
      return t("ai:generate_similar_questions");

    case INCLUDE_REAL_WORLD_EXAMPLES:
      return t("ai:include_real_world_examples_in_the_question");

    case INCREASE_DIFFICULTY:
      return t("ai:increase_difficulty_of_the_question");

    case DECREASE_DIFFICULTY:
      return t("ai:decrease_difficulty_of_the_question");

    case CONVERT_QUESTION_FORMAT:
      return t("ai:convert_the_question_to_type", { type });

    default:
      return "";
  }
};

export const getUpdatedConversationFields = ({
  extraParams,
  params,
  fieldList,
  conversationAllFields = [],
}) => {
  const fieldUID = _.first(_.keys(params));
  const fieldValue = _.get(params, fieldUID, []);

  let newConversationAllFields = [...conversationAllFields];

  // * remove field if value empty
  if (_.isEmpty(fieldValue)) {
    newConversationAllFields = _.filter(
      newConversationAllFields,
      field => !_.isEqual(_.get(field, "uid"), fieldUID)
    );
  }
  // * update or add new field
  else {
    const fieldData = _.find(newConversationAllFields, { uid: fieldUID });

    const resolvedMinimalTree = getResolvedOptimisticValue({
      resolvedValue: _.get(fieldData, "resolvedMinimalTree", {}),
      fieldUID,
      fieldTemplateObj: _.get(fieldList, fieldUID, {}),
      params: extraParams,
      value: fieldValue,
    });

    const newOrUpdatedFieldData = {
      id: _.get(fieldData, "id", generateRandomId()),
      uid: fieldUID,
      value: fieldValue,
      resolvedMinimalTree,
    };

    // * add new field
    if (_.isEmpty(fieldData)) {
      newConversationAllFields.push(newOrUpdatedFieldData);
    }
    // * update field
    else {
      const fieldIndex = _.findIndex(newConversationAllFields, {
        uid: fieldUID,
      });
      newConversationAllFields[fieldIndex] = newOrUpdatedFieldData;
    }
  }

  return newConversationAllFields;
};
